import { createCarouselTimer } from './carousel-timer';

/**
 * Testimonial feature module.
 *
 * The block renders multiple parallel "stacks" — one per visual region (media,
 * quote, attribution text). Each stack contains one element per testimonial,
 * keyed by `data-index`. All items in a stack occupy the same grid cell; only
 * the one with `.active` is visible.
 *
 * Advancing means toggling `.active` (and `aria-hidden`) across every element
 * with a matching `data-index`. The DOM is never mutated, so inline markup
 * inside testimonials (spans, em, strong, etc.) is preserved.
 *
 * The prev/next controls live OUTSIDE every stack — never re-rendered, so the
 * click listeners attach once and stay live for the page lifetime.
 *
 * Auto-advance via the shared `carousel-timer` helper. Manual clicks PAUSE
 * auto-advance and start a 30s idle countdown — once the user stops clicking
 * for 30s straight, auto-advance resumes. Each click resets the countdown.
 */
const RESUME_AFTER_IDLE_MS = 15000;

const initOne = (carousel) => {
    const indexed = carousel.querySelectorAll('[data-index]');
    const prevBtn = carousel.querySelector('.testimonial-feature__prev');
    const nextBtn = carousel.querySelector('.testimonial-feature__next');
    const progressTracker = carousel.querySelector('.testimonial-feature__progress__tracker');

    if (indexed.length === 0) return;

    // Compute total slide count from the unique data-index values across all stacks.
    const indices = new Set();
    indexed.forEach(el => indices.add(parseInt(el.dataset.index, 10)));
    const total = indices.size;

    if (total <= 1) return;

    let currentIndex = 0;

    const updateControlState = (index) => {
        if (prevBtn) prevBtn.disabled = index === 0;
        if (nextBtn) nextBtn.disabled = index === total - 1;
    };

    const setActive = (index) => {
        indexed.forEach(el => {
            const isActive = parseInt(el.dataset.index, 10) === index;
            el.classList.toggle('active', isActive);
            if (isActive) {
                el.removeAttribute('aria-hidden');
            } else {
                el.setAttribute('aria-hidden', 'true');
            }
        });
        currentIndex = index;
        updateControlState(index);
    };

    const advance = (direction = 1) => {
        const next = (currentIndex + direction + total) % total;
        setActive(next);
    };

    // Initialize disabled state for the first slide.
    updateControlState(currentIndex);

    const timer = createCarouselTimer({
        container: carousel,
        progressTracker,
        onAdvance: () => advance(1),
    });

    let resumeTimeout = null;

    const handleManualAdvance = (direction) => {
        advance(direction);
        timer.pause();

        // Reset the idle countdown; resume auto-advance once the user has been
        // quiet for RESUME_AFTER_IDLE_MS straight.
        if (resumeTimeout) clearTimeout(resumeTimeout);
        resumeTimeout = setTimeout(() => {
            resumeTimeout = null;
            timer.start();
        }, RESUME_AFTER_IDLE_MS);
    };

    if (prevBtn) {
        prevBtn.addEventListener('click', () => handleManualAdvance(-1));
    }
    if (nextBtn) {
        nextBtn.addEventListener('click', () => handleManualAdvance(1));
    }
};

export const initTestimonialFeature = () => {
    document.addEventListener('DOMContentLoaded', () => {
        const carousels = document.querySelectorAll('.testimonial-feature__carousel');
        carousels.forEach(initOne);
    });
};
